import React from "react";
import {
	components,
	type GetOptionValue,
	type GroupBase,
	type InputProps,
	type MultiValue,
	type OptionProps,
	type OptionsOrGroups,
	type StylesConfig,
	type ValueContainerProps,
} from "react-select";

import { faSearch } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Checkbox } from "~/ui";

import styles from "./MultiSelector.module.css";

export interface MultiSelectorOption {
	readonly label: string;
	readonly value: string;
	readonly isDisabled?: boolean;
	readonly icon?: IconDefinition;
}

export function sortSelectedToTop<Option extends { label: string; value: string }, Group extends GroupBase<Option>>(
	options: OptionsOrGroups<Option, Group>,
	values: MultiValue<Option>,
	getOptionValue: GetOptionValue<Option>,
	sortSelected: boolean,
) {
	const selectedValues = new Set(values.map(getOptionValue));

	const selectedOptions: Option[] = [],
		unselectedOptions: Option[] = [],
		groups: Group[] = [];

	for (const option of options) {
		if (!("value" in option)) {
			groups.push({
				...option,
				options: sortSelectedToTop(option.options, values, getOptionValue, sortSelected),
			});
		} else if (selectedValues.has(getOptionValue(option))) {
			selectedOptions.push(option);
		} else {
			unselectedOptions.push(option);
		}
	}

	const result: OptionsOrGroups<Option, Group> = [];

	if (sortSelected) selectedOptions.sort((a, b) => (a.label.localeCompare(b.label) ? 1 : -1));

	return result.concat(selectedOptions, unselectedOptions, groups);
}

export function formatSelectorOption(label: string, value: string): MultiSelectorOption {
	const labelWithoutSpaces = label.split(" ").join("");

	if (labelWithoutSpaces === value) {
		return { label, value };
	}

	return { label: `${label} (${value})`, value };
}

export const defaultGetOptionValue: GetOptionValue<{ value: string }> = ({ value }) => value;

export const IndicatorsContainer = () => null;

export const Input = <T extends {}>({ children, ...rest }: InputProps<T, true>) => {
	return (
		<components.Input {...rest} placeholder="Search" className={styles.input}>
			{children}
		</components.Input>
	);
};

export const Option = <
	T extends {
		icon?: IconDefinition;
	},
>({
	children,
	data,
	...rest
}: OptionProps<T, true>) => {
	const { isSelected } = rest;
	const icon = data["icon"];

	return (
		<components.Option {...rest} data={data}>
			<Checkbox checked={isSelected} className={styles.checkbox} />
			{icon && (
				<span className={styles.optionVisual}>
					<FontAwesomeIcon icon={icon} className={styles.optionImage} />
				</span>
			)}
			<span className={styles.optionLabel}>{children}</span>
		</components.Option>
	);
};

export const Placeholder = () => null;

export const ValueContainer = <T extends {}>({ children, ...rest }: ValueContainerProps<T, true>) => {
	return (
		<components.ValueContainer {...rest}>
			<FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
			{children}
		</components.ValueContainer>
	);
};

export const reactSelectStylesRoot: StylesConfig<{ label: string }, true> = {
	control: base => ({
		...base,
		flex: "0 0 auto",
		backgroundColor: "var(--flyoutBackgroundColor)",
		border: "none",
		borderColor: "var(--secondLayerColor)",
		borderBottom: "1px solid var(--dividerColor)",
		borderBottomLeftRadius: 0,
		borderBottomRightRadius: 0,
		borderTopLeftRadius: "0.375rem",
		borderTopRightRadius: "0.375rem",
		minHeight: "unset",
		boxShadow: "none",
		":hover": { borderColor: "var(--secondLayerColor)", borderBottom: "1px solid var(--dividerColor)" },
	}),
	input: base => ({
		...base,
		color: "var(--primaryTextColor)",
		display: "inline-flex",
		fontSize: "14px",
		lineHeight: 1.14,
		margin: 0,
		padding: "0.45rem",
	}),
	container: () => ({
		display: "flex",
		flexDirection: "column",
		maxHeight: "var(--radix-popover-content-available-height)",
	}),
	menu: () => ({
		backgroundColor: "var(--flyoutBackgroundColor)",
		color: "var(--primaryTextColor)",
		fontSize: "14px",
		lineHeight: 1.14,
		margin: 0,
		minHeight: 0,
		flex: "1 1 auto",
		display: "flex",
		flexDirection: "column",
	}),
	menuList: base => ({
		...base,
		minHeight: 0,
	}),
	option: (base, state) => ({
		...base,
		alignItems: "center",
		backgroundColor: state.isFocused ? "var(--baseLayerColor)" : "var(--flyoutBackgroundColor)",
		borderRadius: "6px",
		color: `var(--${state.isDisabled ? "placeholderTextColor" : "primaryTextColor"})`,
		display: "flex",
		padding: "0.375rem 0.75rem",
		":hover": { cursor: state.isDisabled ? "not-allowed" : "pointer" },
		// The below style is specific to the Chrome browser which has a blue background when an option is selected without it
		":active": { backgroundColor: "var(--baseLayerColor)" },
	}),
	valueContainer: base => ({ ...base, display: "inline-flex", padding: 0, flexWrap: "nowrap", width: "100%" }),
	group: () => ({
		color: "var(--secondaryTextColor)",
		"&:not(:first-child)": { borderTop: "1px solid var(--dividerColor)", marginTop: "0.5rem" },
	}),
	groupHeading: () => ({
		padding: "0.5rem 0.75rem 0.375rem",
		fontSize: "0.75rem",
	}),
};
